import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, BarChart3, Calendar, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShowTypeProfitability() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/analytics/show-type-profitability`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading profitability data...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">No data available</div>
      </div>
    );
  }

  const graphics = data.vyrus_graphics;
  const trading = data.vyrus_trading_cards;
  const comparison = data.comparison;

  const winnerIsGraphics = comparison.more_profitable === 'vyrus_graphics';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight" data-testid="profitability-title">
          Show Type Profitability
        </h1>
        <p className="text-muted-foreground mt-1">Compare big shows (full inventory) vs small shows (trading cards only)</p>
      </div>

      {/* Winner Banner */}
      <Card className={`${winnerIsGraphics ? 'bg-purple-500/10 border-purple-500/30' : 'bg-cyan-500/10 border-cyan-500/30'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-full ${winnerIsGraphics ? 'bg-purple-500/20' : 'bg-cyan-500/20'}`}>
              <TrendingUp className={`h-8 w-8 ${winnerIsGraphics ? 'text-purple-400' : 'text-cyan-400'}`} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                {winnerIsGraphics ? 'Vyrus Graphics Shows' : 'Vyrus Trading Card Shows'} are more profitable!
              </h2>
              <p className="text-muted-foreground">
                Average profit per event: {formatCurrency(comparison.profit_difference)} higher
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vyrus Graphics (Big Shows) */}
        <Card className="bg-card border-border/50 border-l-4 border-l-purple-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-400">
              <BarChart3 className="h-5 w-5" />
              {graphics.label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Events</p>
                <p className="text-2xl font-bold">{graphics.total_events}</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Avg Profit/Event</p>
                <p className={`text-2xl font-bold ${graphics.avg_profit_per_event >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(graphics.avg_profit_per_event)}
                </p>
              </div>
              <div className="p-4 bg-green-500/10 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-xl font-bold text-green-400">{formatCurrency(graphics.total_revenue)}</p>
              </div>
              <div className="p-4 bg-red-500/10 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-xl font-bold text-red-400">{formatCurrency(graphics.total_expenses)}</p>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${graphics.total_profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
              <p className="text-sm text-muted-foreground">Total Profit</p>
              <p className={`text-3xl font-bold ${graphics.total_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(graphics.total_profit)}
              </p>
            </div>

            {/* Events List */}
            {graphics.events.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Events</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {graphics.events.map((event) => (
                    <div key={event.id} className="flex justify-between items-center p-2 bg-secondary/20 rounded">
                      <div>
                        <p className="text-sm font-medium">{event.name}</p>
                        <p className="text-xs text-muted-foreground">{event.start_date}</p>
                      </div>
                      <p className={`text-sm font-mono ${event.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(event.profit)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vyrus Trading Cards (Small Shows) */}
        <Card className="bg-card border-border/50 border-l-4 border-l-cyan-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-400">
              <Calendar className="h-5 w-5" />
              {trading.label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Events</p>
                <p className="text-2xl font-bold">{trading.total_events}</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Avg Profit/Event</p>
                <p className={`text-2xl font-bold ${trading.avg_profit_per_event >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(trading.avg_profit_per_event)}
                </p>
              </div>
              <div className="p-4 bg-green-500/10 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-xl font-bold text-green-400">{formatCurrency(trading.total_revenue)}</p>
              </div>
              <div className="p-4 bg-red-500/10 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-xl font-bold text-red-400">{formatCurrency(trading.total_expenses)}</p>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${trading.total_profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
              <p className="text-sm text-muted-foreground">Total Profit</p>
              <p className={`text-3xl font-bold ${trading.total_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(trading.total_profit)}
              </p>
            </div>

            {/* Events List */}
            {trading.events.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Events</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {trading.events.map((event) => (
                    <div key={event.id} className="flex justify-between items-center p-2 bg-secondary/20 rounded">
                      <div>
                        <p className="text-sm font-medium">{event.name}</p>
                        <p className="text-xs text-muted-foreground">{event.start_date}</p>
                      </div>
                      <p className={`text-sm font-mono ${event.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(event.profit)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Insights */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Key Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-secondary/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Total Events Tracked</p>
              <p className="text-xl font-bold">{graphics.total_events + trading.total_events}</p>
            </div>
            <div className="p-4 bg-secondary/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Combined Revenue</p>
              <p className="text-xl font-bold text-green-400">
                {formatCurrency(graphics.total_revenue + trading.total_revenue)}
              </p>
            </div>
            <div className="p-4 bg-secondary/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Combined Profit</p>
              <p className={`text-xl font-bold ${(graphics.total_profit + trading.total_profit) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(graphics.total_profit + trading.total_profit)}
              </p>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-primary/10 rounded-lg">
            <p className="text-sm">
              {graphics.total_events > 0 && trading.total_events > 0 ? (
                <>
                  Based on your data, <strong>{winnerIsGraphics ? 'big shows with full inventory' : 'small trading card shows'}</strong> generate{' '}
                  <strong>{formatCurrency(comparison.profit_difference)}</strong> more profit per event on average.
                  {graphics.total_events < 5 || trading.total_events < 5 ? (
                    <> Consider tracking more events for more accurate insights.</>
                  ) : null}
                </>
              ) : (
                <>Add events with different show types to start tracking profitability comparisons!</>
              )}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
